import type { AdminBillingManager } from '@/state/useAdminBilling';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { PlanChoiceFields } from './PlanChoiceFields';

type ChangePlanDialogProps = { manager: AdminBillingManager };

/**
 * Changes the plan of an invoice or free account. The plan is a label Polar does not hold:
 * the account's sessions are measured against it and its pages name it, and nothing is charged.
 * Its allowance window keeps its start date, so the change is only the label.
 */
export const ChangePlanDialog = ({ manager }: ChangePlanDialogProps) => {
  const { changingPlanFor: company, planChoice, setPlanChoice, planChangeBlockingReason } = manager;

  return (
    <ConfirmDialog
      open={!!company}
      title={`Change plan: ${company?.name ?? ''}`}
      description={
        company?.effectiveBillingMode === 'free'
          ? 'A free account. The plan is a label for its allowance; nothing is charged.'
          : 'Billed by invoice. The plan is what its sessions are measured against; nothing goes to Polar.'
      }
      confirmLabel="Change plan"
      disabled={!!planChangeBlockingReason}
      disabledReason={planChangeBlockingReason}
      onConfirm={manager.confirmChangePlan}
      onCancel={manager.closeChangePlan}
    >
      <PlanChoiceFields
        plan={planChoice.plan}
        interval={planChoice.interval}
        onPlanChange={(plan) => setPlanChoice({ ...planChoice, plan })}
        onIntervalChange={(interval) => setPlanChoice({ ...planChoice, interval })}
        hint="The allowance window keeps its start date; only the label changes."
      />
    </ConfirmDialog>
  );
};
